import { forwardRef } from "react";
import {
  Button as DSButton,
  ButtonLink as DSButtonLink,
  BUTTON_COLOR,
} from "@hireart/component-library";
import clsx from "clsx";
import PropTypes from "prop-types";

const SUI_COLOR_MAP = {
  green: BUTTON_COLOR.PRIMARY_GREEN,
  blue: BUTTON_COLOR.PRIMARY_BLUE,
  red: BUTTON_COLOR.PRIMARY_RED,
  olive: BUTTON_COLOR.PRIMARY_GREEN,
  purple: BUTTON_COLOR.PRIMARY_PURPLE,
  yellow: BUTTON_COLOR.PRIMARY_YELLOW,
  grey: BUTTON_COLOR.SECONDARY,
  gray: BUTTON_COLOR.SECONDARY,
};

const SUI_SIZE_MAP = {
  mini: "sm",
  tiny: "sm",
  small: "sm",
  medium: "md",
  large: "lg",
  big: "xl",
  huge: "xl",
  massive: "xl",
};

function resolveColor({ color, primary, basic, positive, inverted }) {
  if (basic || inverted) return BUTTON_COLOR.SECONDARY;
  if (positive) return BUTTON_COLOR.PRIMARY_GREEN;
  if (color && SUI_COLOR_MAP[color]) return SUI_COLOR_MAP[color];
  if (color) return color;
  if (primary) return BUTTON_COLOR.PRIMARY_PURPLE;
  return BUTTON_COLOR.PRIMARY_GREEN;
}

function resolveSize(size) {
  if (!size) return "lg";
  return SUI_SIZE_MAP[size] || size;
}

/**
 * Adapter wrapping the design system Button with SUI-compatible API.
 *
 * Maps SUI props (primary, basic, fluid, color, size, loading, content,
 * positive, inverted, floated, as, icon) to design system equivalents
 * so consuming code needs zero changes.
 */
export const Button = forwardRef(
  (
    {
      as: Component,
      basic,
      children,
      className,
      color,
      content,
      disabled,
      floated,
      fluid,
      icon,
      inverted,
      labelPosition,
      loading,
      positive,
      primary,
      size,
      ...rest
    },
    ref
  ) => {
    const dsColor = resolveColor({ color, primary, basic, positive, inverted });
    const dsSize = resolveSize(size);

    const mergedClassName = clsx(
      floated === "right" && "float-right",
      floated === "left" && "float-left",
      className
    );

    const buttonContent = content || children;

    const isLink = Component === "a" || rest.href || rest.to;

    if (isLink) {
      return (
        <DSButtonLink
          ref={ref}
          className={mergedClassName}
          color={dsColor}
          isFullWidth={fluid}
          size={dsSize}
          {...rest}
        >
          {buttonContent}
        </DSButtonLink>
      );
    }

    return (
      <DSButton
        ref={ref}
        className={mergedClassName}
        color={dsColor}
        isDisabled={disabled}
        isFullWidth={fluid}
        isLoading={loading}
        size={dsSize}
        {...rest}
      >
        {buttonContent}
      </DSButton>
    );
  }
);

Button.displayName = "Button";

/**
 * Passthrough for SUI Button.Content compound component.
 * Renders a <span> so styled-components wrapping works.
 */
Button.Content = forwardRef(({ children, className, ...rest }, ref) => (
  <span ref={ref} className={className} {...rest}>
    {children}
  </span>
));

Button.Content.displayName = "Button.Content";

Button.Content.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

/* eslint-disable react/boolean-prop-naming -- SUI-compatible API uses non-prefixed booleans */
Button.propTypes = {
  as: PropTypes.elementType,
  basic: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  color: PropTypes.string,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  disabled: PropTypes.bool,
  floated: PropTypes.oneOf(["left", "right"]),
  fluid: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  inverted: PropTypes.bool,
  labelPosition: PropTypes.oneOf(["left", "right"]),
  loading: PropTypes.bool,
  positive: PropTypes.bool,
  primary: PropTypes.bool,
  size: PropTypes.string,
};
/* eslint-enable react/boolean-prop-naming */
